import { Link, useLocation } from "@/lib/router-compat";
import { Facebook } from "lucide-react";
import { OptimizedImage } from "@/components/ui/optimized-image";
import logoDarkBg from "@/assets/logo-dark-bg.png";
import logoDarkBgWebp from "@/assets/logo-dark-bg.webp";
import skillMammothLogo from "@/assets/skillmammoth-logo-white.png";

const services = [
  { name: "Roofing", path: "/roofing" },
  { name: "Siding", path: "/siding" },
  { name: "Gutters", path: "/gutters" },
  { name: "Roof Replacement", path: "/services/roof-replacement" },
  { name: "Roof Repair", path: "/services/roof-repair" },
  { name: "Siding Replacement", path: "/services/siding-replacement" },
  { name: "Siding Repair", path: "/services/siding-repair" },
  { name: "Deck Installation", path: "/services/deck-installation" },
  { name: "Storm Damage", path: "/storm-damage" },
  { name: "Ice Storms", path: "/ice-storms" },
];

const quickLinks = [
  { name: "About Us", path: "/about" },
  { name: "Projects", path: "/projects" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Blog", path: "/blog" },
  { name: "Careers", path: "/careers" },
  { name: "Contact", path: "/contact" },
];

// Sitewide service-area links — every page footer renders all 13 cities
// so each city page receives an inlink from every other page.
const serviceAreas = [
  { name: "Minneapolis", path: "/service-areas/minneapolis" },
  { name: "Anoka", path: "/service-areas/anoka" },
  { name: "Andover", path: "/service-areas/andover" },
  { name: "Blaine", path: "/service-areas/blaine" },
  { name: "Brooklyn Park", path: "/service-areas/brooklyn-park" },
  { name: "Cambridge", path: "/service-areas/cambridge" },
  { name: "Cedar", path: "/service-areas/cedar" },
  { name: "Coon Rapids", path: "/service-areas/coon-rapids" },
  { name: "East Bethel", path: "/service-areas/east-bethel" },
  { name: "Bethel", path: "/service-areas/bethel" },
  { name: "Fridley", path: "/service-areas/fridley" },
  { name: "Isanti", path: "/service-areas/isanti" },
  { name: "Saint Francis", path: "/service-areas/saint-francis" },
  { name: "View All Areas", path: "/service-areas" },
];

export const Footer = () => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  return (
    <footer className="bg-footer text-footer-foreground" role="contentinfo">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Logo & About */}
          <div className="space-y-6">
            <Link to="/" aria-label="Cedar Creek Construction - Home">
              <OptimizedImage
                src={logoDarkBg}
                webpSrc={logoDarkBgWebp}
                alt="Cedar Creek Construction logo"
                className="h-20 w-auto"
                width={160}
                height={80}
              />
            </Link>
            <p className="text-footer-foreground/80 leading-relaxed">
              Quality roofing, siding, and exterior solutions for Minnesota homeowners. Licensed, insured, and committed to excellence.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/cedarcreekconstructioninc"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center hover:bg-secondary transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-footer"
                aria-label="Visit Cedar Creek Construction on Facebook"
              >
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Services */}
          <nav aria-label="Services">
            <h2 className="text-lg font-heading font-semibold text-primary-foreground mb-6">Our Services</h2>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="text-footer-foreground/80 hover:text-secondary transition-colors focus:outline-hidden focus-visible:text-secondary focus-visible:underline"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Quick Links */}
          <nav aria-label="Quick Links">
            <h2 className="text-lg font-heading font-semibold text-primary-foreground mb-6">Quick Links</h2>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-footer-foreground/80 hover:text-secondary transition-colors focus:outline-hidden focus-visible:text-secondary focus-visible:underline"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Service Areas — sitewide internal links */}
          <nav aria-label="Service Areas">
            <h2 className="text-lg font-heading font-semibold text-primary-foreground mb-6">Service Areas</h2>
            <ul className="space-y-3">
              {serviceAreas.map((area) => (
                <li key={area.path}>
                  <Link
                    to={area.path}
                    className="text-footer-foreground/80 hover:text-secondary transition-colors focus:outline-hidden focus-visible:text-secondary focus-visible:underline"
                  >
                    {area.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact Info */}
          <div>
            <h2 className="text-lg font-heading font-semibold text-primary-foreground mb-6">Contact Us</h2>
            <address className="not-italic space-y-4 text-footer-foreground/80">
              <div>
                <span className="block font-medium text-primary-foreground">Phone</span>
                <a 
                  href="tel:+16122989865" 
                  className="hover:text-secondary transition-colors focus:outline-hidden focus-visible:text-secondary focus-visible:underline"
                  aria-label="Call Cedar Creek Construction at (612) 298-9865"
                >
                  (612) 298-9865
                </a>
              </div>
              <div>
                <span className="block font-medium text-primary-foreground">Hours</span>
                Mon–Fri: 6 AM – 9 PM<br />
                Sat: 9 AM – 9 PM<br />
                Sun: Closed
              </div>
              <div>
                <span className="block font-medium text-primary-foreground">Service Area</span>
                Minneapolis, MN and<br />surrounding communities
              </div>
              <div>
                <span className="block font-medium text-primary-foreground">License #</span>
                BC645307
              </div>
            </address>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <p className="text-center text-footer-foreground/60 text-sm flex items-center justify-center gap-1 flex-wrap">
            <span>© 2026 Cedar Creek Construction. License #: BC645307.</span>
            <span className="inline-flex items-center gap-1">
              Website Design & SEO by{" "}
              <a
                href="https://www.skillmammoth.com/"
                target="_blank"
                rel={isHomePage ? "noopener noreferrer" : "noopener noreferrer nofollow"}
                className="inline-flex items-center gap-1.5 text-secondary underline hover:text-secondary/80 focus:outline-hidden focus-visible:text-secondary"
              >
                <img 
                  src={skillMammothLogo} 
                  alt="Skill Mammoth Digital logo" 
                  className="h-5 w-5 inline-block transition-transform duration-200 hover:scale-125"
                  width={20}
                  height={20}
                  loading="lazy"
                />
                Skill Mammoth Digital
              </a>
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
};
